import { useCallback, useEffect, useMemo, useState } from "react";
import { products, storefront } from "../config/storefront.js";

const productById = new Map(products.map((product) => [product.id, product]));

function loadCart() {
  try {
    const stored = window.localStorage.getItem(storefront.cartStorageKey);
    const parsed = stored ? JSON.parse(stored) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item) => productById.has(item.id))
      .map((item) => ({
        id: item.id,
        quantity: Math.max(1, Math.min(99, Number(item.quantity) || 1))
      }));
  } catch {
    return [];
  }
}

function saveCart(cart) {
  try {
    window.localStorage.setItem(storefront.cartStorageKey, JSON.stringify(cart));
  } catch {
    // Local storage can be unavailable in private browsing or blocked contexts.
  }
}

export function useCart() {
  const [cart, setCart] = useState(loadCart);

  useEffect(() => {
    saveCart(cart);
  }, [cart]);

  const addToCart = useCallback((productId, quantity = 1) => {
    setCart((current) => {
      const safeQuantity = Math.max(1, Math.min(99, Number(quantity) || 1));
      const existing = current.find((item) => item.id === productId);
      if (!existing) return [...current, { id: productId, quantity: safeQuantity }];
      return current.map((item) =>
        item.id === productId
          ? { ...item, quantity: Math.min(99, item.quantity + safeQuantity) }
          : item
      );
    });
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const lineItems = useMemo(
    () =>
      cart.flatMap((item) => {
        const product = productById.get(item.id);
        return product ? [{ ...item, product, lineTotalCents: product.priceCents * item.quantity }] : [];
      }),
    [cart]
  );

  const quantity = useMemo(() => cart.reduce((sum, item) => sum + item.quantity, 0), [cart]);
  const totalCents = useMemo(
    () => lineItems.reduce((sum, item) => sum + item.lineTotalCents, 0),
    [lineItems]
  );

  return { cart, lineItems, quantity, totalCents, addToCart, clearCart };
}
